import React, { useState, useEffect, useRef } from 'react';
import {
  MainContainer,
  ChatContainer,
  MessageList,
  Message,
  MessageInput,
} from '@chatscope/chat-ui-kit-react';
import '@chatscope/chat-ui-kit-styles/dist/default/styles.min.css';
import { sendMessage } from '../services/api';
import { logout } from '../utils/auth';
import ChatMessage from '../components/ChatMessage';
import Navbar from '../components/Navbar';
import { useI18n } from '../i18n/I18nContext';
import styles from './Chat.module.css';

function Chat() {
  const { t } = useI18n();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const messagesEndRef = useRef(null);

  // 自動滾動到底部
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async (messageText) => {
    if (!messageText.trim() || loading) return;

    const userMessage = {
      message: messageText,
      sentTime: new Date().toISOString(),
      sender: 'user',
      direction: 'outgoing',
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setLoading(true);

    // 顯示載入訊息
    const loadingMessage = {
      message: t('chat.thinking'),
      sentTime: new Date().toISOString(),
      sender: 'AI',
      direction: 'incoming',
      isLoading: true,
    };
    setMessages((prev) => [...prev, loadingMessage]);

    try {
      const response = await sendMessage(messageText, conversationId);
      const responseData = response.data;
      const aiResponse = typeof responseData === 'string' 
        ? responseData 
        : (responseData.response || responseData.answer || JSON.stringify(responseData));
      const conversation_id = responseData.conversation_id;

      // 移除載入訊息
      setMessages((prev) => prev.filter((msg) => !msg.isLoading));

      // 新增 AI 回應
      const aiMessage = {
        message: String(aiResponse),
        sentTime: new Date().toISOString(),
        sender: 'AI',
        direction: 'incoming',
      };
      setMessages((prev) => [...prev, aiMessage]);

      if (conversation_id) {
        setConversationId(conversation_id);
      }
    } catch (err) {
      // 移除載入訊息
      setMessages((prev) => prev.filter((msg) => !msg.isLoading));

      const errorMessage = {
        message: err.response?.data?.error || t('chat.default_error'),
        sentTime: new Date().toISOString(),
        sender: 'AI',
        direction: 'incoming',
        isError: true,
      };

      setMessages((prev) => [...prev, errorMessage]);

      // 如果是認證錯誤，登出
      if (err.response?.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.chatPage}>
      <Navbar />
      <div style={{ position: 'relative', height: 'calc(100vh - 80px)' }}>
        <MainContainer>
          <ChatContainer>
            <MessageList>
              {messages.map((msg, index) => (
                <Message
                  key={index}
                  model={{
                    message: msg.message, // 始終傳遞字符串
                    sentTime: new Date(msg.sentTime).toLocaleTimeString('ja-JP'),
                    sender: msg.sender === 'user' ? t('chat.sender_you') : 'AI',
                    direction: msg.direction,
                  }}
                >
                    {/* 對於 AI 消息，直接使用 children 來渲染 Markdown */}
                    {msg.sender === 'AI' && !msg.isLoading && !msg.isError ? (
                      <ChatMessage content={msg.message} />
                    ) : null}
                </Message>
              ))}
              <div ref={messagesEndRef} />
            </MessageList>
            <MessageInput
              placeholder={t('chat.placeholder')}
              value={inputValue}
              onChange={(val) => setInputValue(val)}
              onSend={handleSend}
              disabled={loading}
              attachButton={false}
            />
          </ChatContainer>
        </MainContainer>
      </div>
    </div>
  );
}

export default Chat;
